import type { BadgeTier } from "@tacklefour/api-types";

const TIER_COLOURS: Record<BadgeTier, string> = {
  bronze: "#CD7F32",
  silver: "#C0C0C0",
  gold: "#FFD700",
  special: "#FF5733",
};

/**
 * Map icon identifier strings from badge definitions to emoji characters.
 * We use emoji for simplicity — they render well at any size and require no
 * additional assets.
 */
const ICON_MAP: Record<string, string> = {
  football: "\u{1F3C8}",   // 🏈
  target: "\u{1F3AF}",     // 🎯
  sparkles: "\u{2728}",    // ✨
  flame: "\u{1F525}",      // 🔥
  bullseye: "\u{1F3AF}",   // 🎯
  crosshair: "\u{1FA78}",  // 🩸 (closest available — or use 🎯)
  calendar: "\u{1F4C5}",   // 📅
  users: "\u{1F465}",      // 👥
  "plus-circle": "\u{2795}", // ➕
  trophy: "\u{1F3C6}",     // 🏆
  crown: "\u{1F451}",      // 👑
  "user-check": "\u{2705}",// ✅
  share: "\u{1F4E4}",      // 📤
  "fire-emoji": "\u{1F525}", // 🔥
  "two-sports": "\u{1F3C9}", // 🏉
  rocket: "\u{1F680}",     // 🚀
};

export function BadgeIcon({
  icon,
  tier,
  size = "md",
  locked = false,
}: {
  icon: string;
  tier: BadgeTier;
  size?: "sm" | "md" | "lg";
  locked?: boolean;
}) {
  const emoji = ICON_MAP[icon] ?? "\u{1F3C5}"; // 🏅 fallback
  const colour = TIER_COLOURS[tier];

  const sizeClasses: Record<string, string> = {
    sm: "h-8 w-8 text-base",
    md: "h-12 w-12 text-xl",
    lg: "h-16 w-16 text-3xl",
  };

  return (
    <div
      className={`flex items-center justify-center rounded-full ${sizeClasses[size]} ${locked ? "opacity-40 grayscale" : ""}`}
      style={{
        background: locked
          ? "rgba(255,255,255,0.05)"
          : `linear-gradient(135deg, ${colour}33, ${colour}1a)`,
        border: `2px solid ${locked ? "rgba(255,255,255,0.1)" : colour}`,
      }}
    >
      <span role="img" aria-hidden="true">
        {locked ? "🔒" : emoji}
      </span>
    </div>
  );
}

export function getTierColour(tier: BadgeTier): string {
  return TIER_COLOURS[tier];
}

export function getTierLabel(tier: BadgeTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
